import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useRecommendationPolling } from './useRecommendationPolling';
import type { CategoryRecommendationsResponse } from '../types/recommendation.types';

interface UseCategoryRecommendationsParams {
  childId: number | null;
  categoryId: number;
}

interface UseCategoryRecommendationsResult {
  data: CategoryRecommendationsResponse | null;
  isLoading: boolean;
  isRegenerating: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch category page recommendations with automatic polling during generation.
 *
 * Pattern reference: useHomeRecommendations.ts (Phase 2)
 *
 * Behavior:
 * - Fetches recommendations immediately on mount
 * - If isRegenerating=true, polls status every 3 seconds
 * - When status changes to 'completed', refetches recommendations
 * - Shows previous stale recommendations while regenerating
 * - Returns empty array if insufficient recommendations (section hidden in UI)
 *
 * @param params - Child ID and category ID
 * @returns Recommendations data, loading state, regeneration flag, error, refetch function
 */
export function useCategoryRecommendations({
  childId,
  categoryId,
}: UseCategoryRecommendationsParams): UseCategoryRecommendationsResult {
  const [data, setData] = useState<CategoryRecommendationsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch recommendations
  const fetchRecommendations = useCallback(async () => {
    if (!childId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const result = await api.getCategoryRecommendations(childId, categoryId);

      setData(result);
      setIsRegenerating(result.isRegenerating);
      setIsLoading(false);
    } catch (err: any) {
      console.error('[useCategoryRecommendations] Fetch error:', err);
      setError(err.message || 'Failed to fetch category recommendations');
      setIsLoading(false);
    }
  }, [childId, categoryId]);

  // Poll for status updates during regeneration
  const pollStatus = useCallback(async () => {
    if (!childId) return;

    try {
      const status = await api.getCategoryRecommendationStatus(childId, categoryId);

      if (status.status === 'completed' && !status.hasInProgressJob) {
        // Generation complete — fetch fresh recommendations
        setIsRegenerating(false);
        await fetchRecommendations();
      } else if (status.status === 'not_started') {
        // Generation hasn't started yet
        setIsRegenerating(false);
      }
      // else status === 'generating' → keep polling
    } catch (err) {
      // Polling errors are silent — keep trying
      console.debug('[useCategoryRecommendations] Polling error:', err);
    }
  }, [childId, categoryId, fetchRecommendations]);

  // Initial fetch on mount or when params change
  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  // Activate polling when regenerating
  useRecommendationPolling(isRegenerating, pollStatus);

  return {
    data,
    isLoading,
    isRegenerating,
    error,
    refetch: fetchRecommendations,
  };
}
